import { useCallback, useEffect, useMemo, useState } from 'react';
import { CaretLineLeft, SignOut } from '@phosphor-icons/react';
import { AuthContextProvider, TokiAuthProvider, useAuth } from '@toki/auth';
import { usePermissions } from './hooks/usePermissions';
import { RAIL_NARROW, useMediaQuery } from './hooks/useMediaQuery';
import { getModuleIcon } from './icons';
import { getModuleColor } from './config/modules';
import { roleLabel } from './config/roles';
import { getAssistantCount, listAllIncidencias } from './services/api';
import { AdminLogin } from './screens/Login';
import { PortalScreen } from './screens/Portal';
import { IncidenciasScreen } from './screens/Incidencias';
import { InboxScreen } from './screens/Inbox';
import { AreasScreen } from './screens/Areas';
import { UsuariosScreen } from './screens/Usuarios';
import { ClientesScreen } from './screens/Clientes';
import { TecnicosScreen } from './screens/Tecnicos';
import { CategoriasScreen } from './screens/Categorias';
import { RolesScreen } from './screens/Roles';
import { InformesScreen } from './screens/Informes';
import { SlaPacksScreen } from './screens/SlaPacks';
import { AsistentePortalScreen } from './screens/AsistentePortal';
import { ParametrosScreen } from './screens/Parametros';
import { tenantConfig } from './tenant';
import './app.css';

type AdminPage = 'incidencias' | 'asistente' | 'informes' | 'areas' | 'clientes' | 'sla' | 'usuarios' | 'tecnicos' | 'categorias' | 'roles' | 'asistentePortal' | 'parametros';

/** El color de la pastilla del icono NO se declara aquí: sale de
 *  `getModuleColor(id)` (`config/modules.ts`). Es DECORATIVO — la sección se
 *  reconoce por la etiqueta, no por el color. */
type NavLeaf = { id: AdminPage; label: string; permission?: string | null };

/** Un grupo sin `label` no pinta rótulo: es el bloque de arriba. */
type NavGroup = { label?: string; items: NavLeaf[] };

/* El raíl va en DOS grupos desde el 2026-09-25 (decisión de Jose, cierra esa
   parte de K17): arriba el trabajo diario, abajo la administración. El
   documento de diseño usa el mismo patrón —un rótulo en versalitas— para
   separar «Supervisión».
 *
 * ⚠️ Lo que NO se hace es adoptar las entradas del documento: su raíl lleva
 * Métricas, Colas, Personas, Buscar y Conocimiento, y deja fuera Equipos,
 * Categorías, Usuarios y Roles, que hoy funcionan y NO tienen ninguna otra
 * entrada. Copiarlo literalmente las dejaría inaccesibles. Las cinco del
 * documento no existen: ver K29. */
const NAV: NavGroup[] = [
  {
    items: [
      { id: 'incidencias', label: 'Tickets', permission: 'incidencias:view_all' },
      { id: 'asistente', label: 'Asistente', permission: 'incidencias:view_all' },
    ],
  },
  /* Paso 88. El rótulo es el del v6 («Supervisión»), donde vive «Métricas».
     Admin y coordinador; el técnico no tiene `informes:view` y el grupo
     entero desaparece (un rótulo sin entradas no se pinta). */
  {
    label: 'Supervisión',
    items: [{ id: 'informes', label: 'Informes', permission: 'informes:view' }],
  },
  {
    label: 'Administración',
    items: [
      { id: 'areas', label: 'Equipos', permission: 'areas:manage' },
      { id: 'categorias', label: 'Categorías', permission: 'categories:manage' },
      /* Los tres mantenimientos del paso 85a. Clientes y Usuarios (las
         personas de los clientes) los ve también el técnico: da de alta y
         corrige personas. Técnicos es el staff, y sigue en `users:view`. */
      { id: 'clientes', label: 'Clientes', permission: 'clientes:view' },
      /* Paso 89, junto a Clientes porque un pack se asigna a un cliente.
         Solo admin (D1): el resto ve el pack en la ficha y en el ticket. */
      { id: 'sla', label: 'SLA', permission: 'sla:manage' },
      { id: 'usuarios', label: 'Usuarios', permission: 'clientes:view' },
      { id: 'tecnicos', label: 'Técnicos', permission: 'users:view' },
      /* Todo el staff (paso 48a): quien no tiene `system:manage_roles` la ve
         en solo lectura. Aquí solo entra staff, así que sin permiso. */
      { id: 'roles', label: 'Roles', permission: null },
      /* Paso 92a, donde lo pone el v6: tras Roles. ⚠️ NO es «Asistente» (la
         IA de staff, arriba): es el chatbot del PORTAL del cliente y sus
         preguntas frecuentes. Admin y coordinador editan; el técnico lee y
         prueba (D1). */
      { id: 'asistentePortal', label: 'Asistente del portal', permission: 'asistente_portal:view' },
      /* Paso 101: los parámetros operativos, como en Toki. Solo admin. */
      { id: 'parametros', label: 'Parámetros', permission: 'system:manage_settings' },
    ],
  },
];

/** La preferencia de colapso se recuerda por navegador. Clave propia con
 *  prefijo `kivai.`: el portal comparte origen con el admin (K24), así que
 *  las claves de `localStorage` son de los dos y no se reutilizan. */
const RAIL_KEY = 'kivai.admin.railCollapsed';

function AdminShell() {
  const { state: authState, provider } = useAuth();
  const { hasPermission } = usePermissions();
  const [page, setPage] = useState<AdminPage>('incidencias');
  const [assistantUnread, setAssistantUnread] = useState(0);
  const [ticketCount, setTicketCount] = useState<number | null>(null);

  // Colapso del raíl. Dos señales distintas y no se mezclan: `railCollapsed`
  // es lo que ha pedido la persona y `narrow` es lo que impone la ventana.
  // Se guardan aparte para que al ensanchar el navegador vuelva el raíl que
  // esa persona tenía, no el que le impuso el tamaño.
  const [railCollapsed, setRailCollapsed] = useState(() => {
    try {
      return localStorage.getItem(RAIL_KEY) === 'true';
    } catch {
      // Ventana privada o almacenamiento bloqueado: el raíl arranca abierto.
      return false;
    }
  });
  const narrow = useMediaQuery(RAIL_NARROW);

  const toggleRail = () => {
    setRailCollapsed((v) => {
      const next = !v;
      try {
        localStorage.setItem(RAIL_KEY, String(next));
      } catch {
        // Se pierde la preferencia entre recargas; el raíl sigue funcionando.
      }
      return next;
    });
  };

  /* ── El enlace mágico aterriza aquí ────────────────────────────
     Desde el paso 38 el correo lleva a esta app (antes, a `apps/portal` en
     otro puerto). Si la URL trae `?token=`, se canjea antes de decidir qué
     pintar.

     ⚠️ El token se quita de la URL con `replaceState` en cuanto se usa, salga
     bien o mal: un enlace de acceso en la barra de direcciones acaba en el
     historial, en lo que se comparte por captura y en el `Referer` de
     cualquier recurso externo que cargue la página. Es de un solo uso y dura
     15 minutos, pero eso no es motivo para dejarlo a la vista. */
  const [canjeando, setCanjeando] = useState(() => new URLSearchParams(window.location.search).has('token'));

  useEffect(() => {
    const url = new URL(window.location.href);
    const linkToken = url.searchParams.get('token');
    if (!linkToken) return;
    url.searchParams.delete('token');
    window.history.replaceState({}, '', url.pathname + url.search);
    provider
      .loginWithPortalLink?.(linkToken)
      .catch(() => {
        // Da igual por qué falló —caducado, ya usado, inventado—: se cae a la
        // puerta, que es donde se pide otro. El backend ya responde lo mismo
        // en los cuatro casos a propósito.
      })
      .finally(() => setCanjeando(false));
  }, [provider]);

  const isStaff =
    authState.status === 'authenticated' &&
    authState.user?.role !== 'requester' &&
    (authState.user?.permissions?.length ?? 0) > 0;

  const loadRailCounts = useCallback(async () => {
    if (!isStaff) return;
    const token = await provider.getAccessToken().catch(() => null);
    if (!token) return;
    // Dos contadores independientes: que falle uno no debe dejar el otro
    // sin pintar, así que NO comparten try. `limit: 1` porque solo se usa
    // el `total` — no hace falta traerse la página entera.
    try {
      setAssistantUnread(await getAssistantCount(token));
    } catch {
      // Non-fatal — el badge simplemente no se muestra.
    }
    try {
      setTicketCount((await listAllIncidencias(token, { limit: 1 })).total);
    } catch {
      setTicketCount(null);
    }
  }, [isStaff, provider]);

  useEffect(() => {
    loadRailCounts();
  }, [loadRailCounts, page]);

  if (authState.status === 'loading' || canjeando) {
    return <div className="admin-loading">Cargando...</div>;
  }

  /* ⚠️ Aquí se bifurca por ROL, y es el corazón de «una puerta para todos»
     (pasos 37 y 38). Quien está autenticado y NO es staff ve el PORTAL, no un
     mensaje de «no tienes permisos» — que es lo que había antes, cuando el
     portal era otra aplicación en otro puerto.

     `isStaff` mira el rol Y que tenga permisos: un requester nunca los tiene.
     La frontera real no está en esta línea, sino en el backend —`/auth/login`
     solo autentica a staff y `/incidencias/mine` solo devuelve lo de quien
     pregunta—; esto decide qué se PINTA, no a qué se tiene acceso. */
  if (authState.status === 'authenticated' && !isStaff) {
    return <PortalScreen />;
  }

  if (!isStaff) {
    return <AdminLogin />;
  }

  const handleLogout = async () => {
    await provider.logout();
  };

  const canSee = (leaf: NavLeaf) => leaf.permission == null || hasPermission(leaf.permission);
  // Un grupo cuyas entradas se filtran todas NO debe dejar su rótulo
  // colgando: «ADMINISTRACIÓN» encima de nada es peor que no separar.
  const nav = NAV.map((g) => ({ ...g, items: g.items.filter(canSee) })).filter((g) => g.items.length > 0);

  const displayName = authState.user?.displayName ?? '';
  const initials = displayName.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2) || '?';
  const roleText = roleLabel(authState.user?.role);

  // Raíl: pastilla de icono + etiqueta + contador sobre fondo oscuro, según
  // el documento de diseño (2026-09-25). Lo que marca el ítem activo es el
  // TEXTO blanco (17.16:1), no el fondo: el velo blanco al 10 % es 1.35:1 y
  // por sí solo no sería perceptible. Cuando el raíl se colapsa no hay
  // etiqueta, así que la señal pasa a la pastilla — ver `app.css`.
  //
  // Dos cosas distintas a la derecha, y no se mezclan: `count` es «cuántos
  // hay» (dato, gris) y `badge` es «cuántos sin leer» (alerta, píldora rosa
  // rellena — sobre el raíl oscuro la que destaca es la clara, no la oscura).
  const renderRailItem = (item: NavLeaf) => {
    const active = page === item.id;
    const Icon = getModuleIcon(item.id);
    const unread = item.id === 'asistente' ? assistantUnread : 0;
    const count = item.id === 'incidencias' ? ticketCount : null;
    return (
      <button
        key={item.id}
        type="button"
        className={`admin-rail-item ${active ? 'active' : ''}`}
        onClick={() => setPage(item.id)}
        aria-current={active ? 'page' : undefined}
        /* Con el raíl colapsado la etiqueta se oculta con `display: none`, y
           eso NO es solo visual: se lleva por delante el nombre accesible del
           botón, que pasa a ser un icono sin texto. Con `aria-label` el nombre
           es el mismo en los dos modos. Se detectó porque Playwright dejó de
           encontrar los botones por su nombre en el raíl estrecho — y ahora
           importa más, porque el raíl también se colapsa a mano. */
        aria-label={item.label}
        title={item.label}
        /* El par de color de la pastilla sale de `config/modules.ts`, que es
           la FUENTE ÚNICA: la misma que usa la cinta de cabecera de cada
           pantalla. Antes había un juego de tokens `--tile-*` paralelo en
           `admin-tokens.css` y las dos copias ya habían divergido. */
        style={{
          '--tile-bg': getModuleColor(item.id).bg,
          '--tile-fg': getModuleColor(item.id).accent,
        } as React.CSSProperties}
      >
        <span className="admin-rail-icon">
          <Icon weight={active ? 'fill' : 'regular'} size={19} />
        </span>
        <span className="admin-rail-label">{item.label}</span>
        {unread > 0 ? (
          <span className="admin-rail-badge admin-numeric">{unread}</span>
        ) : count != null ? (
          <span className="admin-rail-count admin-numeric">{count}</span>
        ) : null}
      </button>
    );
  };

  return (
    <div className="admin-frame">
      <nav
        className="admin-rail"
        aria-label="Navegación principal"
        data-collapsed={railCollapsed ? 'true' : 'false'}
        data-narrow={narrow ? 'true' : 'false'}
      >
        <div className="admin-rail-head">
          {/* Dos marcas, una visible cada vez (las alterna el CSS según
              `data-collapsed`/`data-narrow`): el wordmark en el raíl ancho y
              el símbolo suelto en el estrecho. La segunda lleva aria-hidden
              para que un lector de pantalla no anuncie la marca dos veces.

              ⚠️ Se usan las variantes `…OnDark` porque el raíl es oscuro, y
              eso es un dato del TENANT, no una ruta escrita aquí: el mismo
              gancho tiene que servir cuando Kivai se instale con la marca de
              otro (C4/99). Las claras están declaradas al lado en el
              `config.json` para cuando el raíl vuelva a ser claro — que ya ha
              pasado dos veces. */}
          <img
            className="admin-rail-mark"
            src={tenantConfig.logoOnDark}
            alt={tenantConfig.logoAlt}
            title={tenantConfig.displayNameLong ?? tenantConfig.displayName}
          />
          <img
            className="admin-rail-mark-sm"
            src={tenantConfig.logoMarkOnDark}
            alt=""
            aria-hidden="true"
          />
          {/* Por debajo de 1100px el colapso lo manda la ventana, así que el
              botón se esconde (CSS) — pero también se marca `aria-expanded`
              para que el estado sea legible sin ver el chevron. */}
          <button
            type="button"
            className="admin-rail-toggle"
            onClick={toggleRail}
            aria-expanded={!railCollapsed}
            aria-controls="admin-rail-items"
            title={railCollapsed ? 'Expandir el menú' : 'Contraer el menú'}
            aria-label={railCollapsed ? 'Expandir el menú' : 'Contraer el menú'}
          >
            <CaretLineLeft weight="regular" size={15} />
          </button>
        </div>

        <div className="admin-rail-items" id="admin-rail-items">
          {nav.map((group, gi) => {
            if (!group.label) return group.items.map(renderRailItem);
            // El rótulo es el nombre accesible del grupo, no un titular
            // suelto: así un lector de pantalla dice «Administración, grupo»
            // en vez de leerlo como una línea de texto entre botones. Y sigue
            // anunciándose cuando el raíl está colapsado y el rótulo se queda
            // en `font-size: 0`, que es cuando más falta hace.
            const id = `admin-rail-group-${gi}`;
            return (
              <div key={id} role="group" aria-labelledby={id} className="admin-rail-section">
                <div className="admin-rail-group" id={id}>{group.label}</div>
                {group.items.map(renderRailItem)}
              </div>
            );
          })}
        </div>

        <div className="admin-rail-foot">
          <div className="admin-avatar" title={`${displayName} · ${roleText}`}>{initials}</div>
          <div className="admin-rail-user">
            <span className="admin-rail-user-name">{displayName}</span>
            <span className="admin-rail-user-role">{roleText}</span>
          </div>
          <button type="button" className="admin-icon-btn" onClick={handleLogout} title="Cerrar sesión">
            <SignOut weight="regular" size={16} />
          </button>
        </div>
      </nav>

      <main className="admin-content">
        {page === 'incidencias' && <IncidenciasScreen />}
        {page === 'asistente' && <InboxScreen onUnreadChange={setAssistantUnread} />}
        {page === 'informes' && <InformesScreen />}
        {page === 'areas' && <AreasScreen />}
        {page === 'clientes' && <ClientesScreen />}
        {page === 'sla' && <SlaPacksScreen />}
        {page === 'usuarios' && <UsuariosScreen />}
        {page === 'tecnicos' && <TecnicosScreen />}
        {page === 'categorias' && <CategoriasScreen />}
        {page === 'roles' && <RolesScreen />}
        {page === 'asistentePortal' && <AsistentePortalScreen />}
        {page === 'parametros' && <ParametrosScreen />}
      </main>
    </div>
  );
}

export function App() {
  const auth = useMemo(() => new TokiAuthProvider({ baseUrl: '' }), []);

  return (
    <AuthContextProvider provider={auth}>
      <AdminShell />
    </AuthContextProvider>
  );
}
